import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";
import Link from "@cloudscape-design/components/link";
import FormField from "@cloudscape-design/components/form-field";
import FileUpload from "@cloudscape-design/components/file-upload";
import Textarea from "@cloudscape-design/components/textarea";
import Select from "@cloudscape-design/components/select";
import Checkbox from "@cloudscape-design/components/checkbox";
import Button from "@cloudscape-design/components/button";
import Alert from "@cloudscape-design/components/alert";
import Modal from "@cloudscape-design/components/modal";
import Box from "@cloudscape-design/components/box";
import CONFIG from "../config";

function DocumentUpload({ idToken, onUploadSuccess, onLogout, onNavigateToUploads, onDocumentProcessed }) {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [description, setDescription] = useState("");
  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedType, setSelectedType] = useState({ label: t("venue.documents.upload.autoDetect"), value: "auto-detect" });
  const [autoProcess, setAutoProcess] = useState(true);
  const [useCustomPrompt, setUseCustomPrompt] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [customFormat, setCustomFormat] = useState({ label: "JSON", value: "json" });
  const [promptHistory, setPromptHistory] = useState([]);
  const [historyModal, setHistoryModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [alert, setAlert] = useState(null);
  const [cameraModal, setCameraModal] = useState(false);
  const [stream, setStream] = useState(null);
  const [capturedPreview, setCapturedPreview] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const formatOptions = [
    { label: "JSON", value: "json" },
    { label: "CSV", value: "csv" },
    { label: "Text", value: "text" },
    { label: "Markdown", value: "markdown" },
    { label: "Table", value: "table" },
  ];

  const HISTORY_KEY = "customPromptHistory";
  const MAX_HISTORY = 10;
  const INSTRUCTIONS_KEY = "idp-demo-custom-instructions";

  // Upload status tracking
  const [uploadedDocIds, setUploadedDocIds] = useState([]);
  const [uploadStatuses, setUploadStatuses] = useState([]);
  const [notifiedDocIds, setNotifiedDocIds] = useState(new Set());
  const [savedInstructions, setSavedInstructions] = useState([]);
  const [selectedSample, setSelectedSample] = useState(null);

  // Sample document options
  const sampleDocumentOptions = [
    { label: "Invoice - Acme Solutions", value: "invoice_acme_solutions.pdf" },
    { label: "Receipt - Fresh Mart", value: "receipt_fresh_mart.pdf" },
    { label: "Contract - Service Agreement", value: "contract_service_agreement.pdf" },
    { label: "Medical Form - Patient Intake", value: "medical_form_patient_intake.pdf" },
    { label: "Payslip - April 2026", value: "payslip_april_2026.pdf" },
    { label: "ID Card - Employee", value: "id_card_employee.pdf" },
    { label: "Purchase Order - Mining", value: "purchase_order_mining.pdf" },
  ];

  // Handle sample document selection
  const handleSampleSelect = async (option) => {
    setSelectedSample(option);
    if (!option) {
      return;
    }
    try {
      const response = await fetch(`${process.env.PUBLIC_URL}/samples/${option.value}`);
      const blob = await response.blob();
      const file = new File([blob], option.value, { type: "application/pdf" });
      setFiles([file]);
    } catch (e) {
      console.error("Failed to load sample document:", e);
      setAlert({ type: "error", message: "Failed to load sample document" });
    }
  };

  const handleUnauthorized = useCallback(() => {
    if (onLogout) {
      onLogout();
    }
  }, [onLogout]);

  const loadDocumentTypes = useCallback(async () => {
    try {
      const response = await fetch(`${CONFIG.apiUrl}document-types`, {
        headers: { Authorization: idToken },
      });

      if (response.status === 401 || response.status === 403) {
        handleUnauthorized();
        return;
      }

      if (response.ok) {
        const data = await response.json();
        const options = data.document_types.map((type) => ({
          label: type.name,
          value: type.id,
          description: type.description,
        }));
        setDocumentTypes([
          { label: t("venue.documents.upload.autoDetect"), value: "auto-detect", description: t("venue.documents.upload.autoDetectDescription") },
          ...options,
        ]);
      }
    } catch (error) {
      console.error("Failed to load document types:", error);
      if (error.message === "Failed to fetch") {
        handleUnauthorized();
      }
    }
  }, [idToken, t, handleUnauthorized]);

  // Load prompt history from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY);
    if (savedHistory) {
      try {
        const history = JSON.parse(savedHistory);
        setPromptHistory(history);
      } catch (error) {
        console.error("Failed to load prompt history:", error);
      }
    }
  }, []);

  // Load saved instructions from localStorage
  useEffect(() => {
    const loadSaved = () => {
      try {
        const saved = localStorage.getItem(INSTRUCTIONS_KEY);
        if (saved) setSavedInstructions(JSON.parse(saved));
        else setSavedInstructions([]);
      } catch (e) {
        console.error("Failed to load saved instructions:", e);
      }
    };
    loadSaved();
    // Re-check periodically so instructions created in Configuration tab are picked up
    const interval = setInterval(loadSaved, 2000);
    window.addEventListener("storage", loadSaved);
    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", loadSaved);
    };
  }, []);

  // Poll for upload status after successful upload
  useEffect(() => {
    if (uploadedDocIds.length === 0) return;
    const pollStatus = async () => {
      const newStatuses = [];
      for (const docId of uploadedDocIds) {
        try {
          const response = await fetch(`${CONFIG.apiUrl}documents/${docId}`, {
            headers: { Authorization: idToken },
          });
          if (response.ok) {
            const data = await response.json();
            const doc = data.document || data;
            newStatuses.push({
              id: docId,
              fileName: doc.file_name,
              status: doc.processing_status,
            });
          }
        } catch (e) {
          // ignore polling errors
        }
      }
      setUploadStatuses(newStatuses);
      // Notify parent when a document finishes processing successfully (only once per doc)
      const completed = newStatuses.filter((s) => s.status === "completed" && !notifiedDocIds.has(s.id));
      if (completed.length > 0 && onDocumentProcessed) {
        onDocumentProcessed(completed[0].id);
        setNotifiedDocIds((prev) => new Set([...prev, ...completed.map((s) => s.id)]));
      }
      // Remove completed/failed docs from polling after showing status
      const done = newStatuses.filter((s) => s.status === "completed" || s.status === "failed");
      if (done.length === uploadedDocIds.length) {
        // All done, stop polling after a delay
        setTimeout(() => setUploadedDocIds([]), 5000);
      }
    };
    const interval = setInterval(pollStatus, 3000);
    pollStatus(); // immediate first check
    return () => clearInterval(interval);
  }, [uploadedDocIds, idToken]);

  // Save prompt to history
  const saveToHistory = useCallback((prompt, format) => {
    if (!prompt.trim()) return;

    const newEntry = {
      prompt: prompt.trim(),
      format: format.value,
      formatLabel: format.label,
      timestamp: new Date().toISOString(),
    };

    setPromptHistory((prevHistory) => {
      // Check if this exact combination already exists
      const exists = prevHistory.some((entry) => entry.prompt === newEntry.prompt && entry.format === newEntry.format);

      let newHistory;
      if (exists) {
        // Move existing entry to the front
        newHistory = [newEntry, ...prevHistory.filter((entry) => !(entry.prompt === newEntry.prompt && entry.format === newEntry.format))];
      } else {
        // Add new entry to the front
        newHistory = [newEntry, ...prevHistory];
      }

      // Keep only the last MAX_HISTORY entries
      newHistory = newHistory.slice(0, MAX_HISTORY);

      // Save to localStorage
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory));

      return newHistory;
    });
  }, []);

  // Load most recent prompt when checkbox is enabled
  const handleUseCustomPromptChange = useCallback(
    (checked) => {
      setUseCustomPrompt(checked);

      if (checked && promptHistory.length > 0) {
        // Load the most recent prompt
        const mostRecent = promptHistory[0];
        setCustomPrompt(mostRecent.prompt);
        setCustomFormat({
          label: mostRecent.formatLabel,
          value: mostRecent.format,
        });
      }
    },
    [promptHistory],
  );

  // Load a prompt from history
  const loadFromHistory = useCallback((historyEntry) => {
    setCustomPrompt(historyEntry.prompt);
    setCustomFormat({
      label: historyEntry.formatLabel,
      value: historyEntry.format,
    });
    setHistoryModal(false);
  }, []);

  useEffect(() => {
    loadDocumentTypes();
  }, [loadDocumentTypes]);

  const handleUpload = async () => {
    if (files.length === 0) {
      setAlert({ type: "error", message: t("venue.documents.upload.errorSelectFile") });
      return;
    }

    setLoading(true);
    setDisclaimerAccepted(false);
    const uploadedDocs = [];

    try {
      for (const file of files) {
        const reader = new FileReader();
        const fileContent = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result.split(",")[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const response = await fetch(`${CONFIG.apiUrl}documents`, {
          method: "POST",
          headers: {
            Authorization: idToken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            file_name: file.name,
            file_content: fileContent,
            file_type: file.type,
            description: description,
            document_type_id: selectedType?.value || "",
            auto_process: autoProcess,
            custom_prompt: useCustomPrompt ? customPrompt : undefined,
            custom_format: useCustomPrompt ? customFormat?.value : undefined,
          }),
        });

        if (response.status === 401 || response.status === 403) {
          handleUnauthorized();
          return;
        }

        if (response.ok) {
          const data = await response.json();
          uploadedDocs.push(data.document);
        } else {
          throw new Error(t("venue.documents.upload.errorUploadFailed", { fileName: file.name }));
        }
      }

      // Save custom prompt to history if used
      if (useCustomPrompt && customPrompt.trim()) {
        saveToHistory(customPrompt, customFormat);
      }

      const successMsg = autoProcess
        ? t("venue.documents.upload.successMessageWithProcessing", { count: uploadedDocs.length })
        : t("venue.documents.upload.successMessage", { count: uploadedDocs.length });
      setAlert({ type: "success", message: successMsg });
      setFiles([]);
      setSelectedSample(null);
      setDescription("");
      setSelectedType({ label: t("venue.documents.upload.autoDetect"), value: "auto-detect" });
      setAutoProcess(true);
      setUseCustomPrompt(false);
      setCustomPrompt("");
      setCustomFormat({ label: "JSON", value: "json" });

      if (onUploadSuccess) {
        onUploadSuccess(uploadedDocs);
      }

      // Track uploaded doc IDs for status polling
      if (autoProcess && uploadedDocs.length > 0) {
        setUploadedDocIds(uploadedDocs.map((d) => d.id));
      }
    } catch (error) {
      setAlert({ type: "error", message: error.message });
    } finally {
      setLoading(false);
    }
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setCameraModal(true);
    } catch (error) {
      setAlert({ type: "error", message: "Unable to access camera: " + error.message });
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setCapturedPreview(null);
    setCameraModal(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext("2d");
      context.drawImage(video, 0, 0);

      // Show preview instead of immediately adding to files
      const dataUrl = canvas.toDataURL("image/jpeg");
      setCapturedPreview(dataUrl);

      // Stop the camera stream while previewing
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
        setStream(null);
      }
    }
  };

  const acceptPhoto = () => {
    if (capturedPreview && canvasRef.current) {
      canvasRef.current.toBlob((blob) => {
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        const file = new File([blob], `photo-${timestamp}.jpg`, { type: "image/jpeg" });
        setFiles([...files, file]);
        setCapturedPreview(null);
        setCameraModal(false);
        setAlert({ type: "success", message: t("venue.camera.photoCaptured") });
      }, "image/jpeg");
    }
  };

  const retakePhoto = async () => {
    setCapturedPreview(null);
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      setAlert({ type: "error", message: "Unable to access camera: " + error.message });
    }
  };

  return (
    <Container header={<Header variant="h2">{t("venue.documents.upload.title")}</Header>}>
      <SpaceBetween size="l">
        {alert && (
          <Alert type={alert.type} dismissible onDismiss={() => setAlert(null)}>
            {alert.message}
          </Alert>
        )}

        {uploadStatuses.length > 0 && (
          <SpaceBetween size="xs">
            {uploadStatuses.map((s) => (
              <Alert
                key={s.id}
                type={s.status === "completed" ? "success" : s.status === "failed" ? "error" : "info"}
                dismissible
                onDismiss={() => {
                  setUploadStatuses((prev) => prev.filter((x) => x.id !== s.id));
                  setUploadedDocIds((prev) => prev.filter((id) => id !== s.id));
                }}
              >
                {s.status === "completed" ? (
                  <Link
                    onFollow={() => {
                      if (onNavigateToUploads) onNavigateToUploads();
                    }}
                  >
                    {t("venue.upload-status.completed", { fileName: s.fileName })}
                  </Link>
                ) : s.status === "failed" ? (
                  t("venue.upload-status.failed", { fileName: s.fileName })
                ) : (
                  t("venue.upload-status.processing", { fileName: s.fileName })
                )}
              </Alert>
            ))}
          </SpaceBetween>
        )}

        <FormField label={t("venue.documents.upload.sampleDocument", "Sample Document")} description={t("venue.documents.upload.sampleDescription", "Select a pre-loaded sample document for testing, or upload your own below.")}>
          <Select
            selectedOption={selectedSample}
            onChange={({ detail }) => handleSampleSelect(detail.selectedOption)}
            options={sampleDocumentOptions}
            placeholder={t("venue.documents.upload.samplePlaceholder", "Choose a sample document...")}
          />
        </FormField>

        <FormField label={t("venue.documents.upload.selectFiles")} description={t("venue.documents.upload.fileDescription")}>
          <SpaceBetween size="s">
            <FileUpload
              value={files}
              onChange={({ detail }) => setFiles(detail.value)}
              multiple
              showFileSize
              showFileLastModified
              accept=".pdf,.png,.jpg,.jpeg,.gif,.doc,.docx,.txt,.csv"
              constraintText={t("venue.documents.upload.maxFileSize")}
              disabled={!!selectedSample}
              i18nStrings={{
                uploadButtonText: (multiple) => t("venue.documents.upload.selectFiles"),
                dropzoneText: (multiple) => (multiple ? t("venue.documents.upload.dropzoneMultiple") : t("venue.documents.upload.dropzoneSingle")),
                removeFileAriaLabel: (fileIndex) => `Remove file ${fileIndex + 1}`,
                limitShowFewer: "Show fewer files",
                limitShowMore: "Show more files",
                errorIconAriaLabel: "Error",
                warningIconAriaLabel: "Warning",
              }}
            />
            <Button iconName="add-plus" onClick={startCamera} disabled={!!selectedSample}>
              {t("venue.camera.takePhoto")}
            </Button>
          </SpaceBetween>
        </FormField>

        <FormField label={t("venue.documents.upload.documentType")} description={t("venue.documents.upload.documentTypeDescription")}>
          <Select
            selectedOption={selectedType}
            onChange={({ detail }) => setSelectedType(detail.selectedOption)}
            options={documentTypes}
            placeholder={t("venue.documents.upload.chooseDocumentType")}
          />
        </FormField>

        <FormField label={t("venue.documents.upload.description")} description={t("venue.documents.upload.descriptionHelp")}>
          <Textarea
            value={description}
            onChange={({ detail }) => setDescription(detail.value)}
            placeholder={t("venue.documents.upload.descriptionPlaceholder")}
            rows={3}
          />
        </FormField>

        <Checkbox checked={autoProcess} onChange={({ detail }) => setAutoProcess(detail.checked)}>
          {t("venue.documents.upload.autoProcess")}
        </Checkbox>

        <Checkbox
          checked={useCustomPrompt}
          onChange={({ detail }) => handleUseCustomPromptChange(detail.checked)}
          description={t("venue.documents.upload.customPromptDescription", "Provide custom instructions for AI processing with specific output format")}
        >
          {t("venue.documents.upload.useCustomPrompt", "Use custom processing instructions")}
        </Checkbox>

        {useCustomPrompt && (
          <SpaceBetween size="m">
            {savedInstructions.length > 0 && (
              <FormField label={t("venue.config.instructions.savedInstructions")} description={t("venue.config.instructions.selectInstruction")}>
                <Select
                  selectedOption={null}
                  onChange={({ detail }) => {
                    const instruction = savedInstructions.find((i) => i.id === detail.selectedOption.value);
                    if (instruction) {
                      setCustomPrompt(instruction.prompt);
                      const fmt = formatOptions.find((o) => o.value === instruction.format);
                      if (fmt) setCustomFormat(fmt);
                    }
                  }}
                  options={savedInstructions.map((i) => ({ label: i.name, value: i.id, description: i.formatLabel }))}
                  placeholder={t("venue.config.instructions.selectInstruction")}
                />
              </FormField>
            )}

            {promptHistory.length > 0 && (
              <Box>
                <Button onClick={() => setHistoryModal(true)} iconName="undo">
                  {t("venue.documents.upload.history", "History")} ({promptHistory.length})
                </Button>
              </Box>
            )}

            <FormField
              label={t("venue.documents.upload.outputFormat", "Output Format")}
              description={t("venue.documents.upload.outputFormatDescription", "Choose how you want the extracted data formatted")}
            >
              <Select selectedOption={customFormat} onChange={({ detail }) => setCustomFormat(detail.selectedOption)} options={formatOptions} />
            </FormField>

            <FormField
              label={t("venue.documents.upload.customPromptLabel", "Processing Instructions")}
              description={t("venue.documents.upload.customPromptHelp", "Describe what information to extract and how to format it")}
            >
              <Textarea
                value={customPrompt}
                onChange={({ detail }) => setCustomPrompt(detail.value)}
                placeholder={t(
                  "venue.documents.upload.customPromptPlaceholder",
                  "Example: Extract all line items as CSV with columns: Item, Description, Quantity, Price, Total",
                )}
                rows={4}
              />
            </FormField>
          </SpaceBetween>
        )}

        <Checkbox checked={disclaimerAccepted} onChange={({ detail }) => setDisclaimerAccepted(detail.checked)}>
          {t("venue.documents.upload.disclaimer", "I confirm that this document contains no PII or private data and that I have the right to upload it.")}
        </Checkbox>

        <Button variant="primary" onClick={handleUpload} loading={loading} disabled={files.length === 0 || !disclaimerAccepted}>
          {t("venue.documents.upload.uploadButton")} {files.length > 0 && `(${files.length} ${files.length === 1 ? "file" : "files"})`}
        </Button>
      </SpaceBetween>

      <Modal
        visible={cameraModal}
        onDismiss={stopCamera}
        header={capturedPreview ? t("venue.camera.reviewPhoto") : t("venue.camera.takePhoto")}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={stopCamera}>
                {t("venue.common.cancel")}
              </Button>
              {capturedPreview ? (
                <>
                  <Button iconName="undo" onClick={retakePhoto}>
                    {t("venue.camera.retake")}
                  </Button>
                  <Button variant="primary" iconName="status-positive" onClick={acceptPhoto}>
                    {t("venue.camera.usePhoto")}
                  </Button>
                </>
              ) : (
                <Button variant="primary" iconName="status-positive" onClick={capturePhoto}>
                  {t("venue.camera.capture")}
                </Button>
              )}
            </SpaceBetween>
          </Box>
        }
        size="large"
      >
        <SpaceBetween size="m">
          {capturedPreview ? (
            <img
              src={capturedPreview}
              alt={t("venue.camera.preview")}
              style={{ width: "100%", maxHeight: "500px", objectFit: "contain", borderRadius: "8px" }}
            />
          ) : (
            <video ref={videoRef} autoPlay playsInline style={{ width: "100%", maxHeight: "500px", backgroundColor: "#000" }} />
          )}
          <canvas ref={canvasRef} style={{ display: "none" }} />
        </SpaceBetween>
      </Modal>

      <Modal
        visible={historyModal}
        onDismiss={() => setHistoryModal(false)}
        header={t("venue.documents.upload.promptHistory", "Custom Prompt History")}
        footer={
          <Box float="right">
            <Button variant="link" onClick={() => setHistoryModal(false)}>
              {t("venue.common.close")}
            </Button>
          </Box>
        }
        size="large"
      >
        <SpaceBetween size="m">
          {promptHistory.length === 0 ? (
            <Box textAlign="center" color="text-body-secondary">
              {t("venue.documents.upload.noHistory", "No prompt history yet")}
            </Box>
          ) : (
            promptHistory.map((entry, index) => (
              <Container
                key={index}
                header={
                  <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                    <Box variant="h3">
                      {entry.formatLabel} - {new Date(entry.timestamp).toLocaleDateString()}
                    </Box>
                    <Button variant="primary" iconName="upload" onClick={() => loadFromHistory(entry)}>
                      {t("venue.documents.upload.useThisPrompt", "Use this prompt")}
                    </Button>
                  </SpaceBetween>
                }
              >
                <Box variant="p" color="text-body-secondary" fontSize="body-s">
                  {new Date(entry.timestamp).toLocaleString()}
                </Box>
                <Box variant="p" padding={{ top: "s" }}>
                  {entry.prompt}
                </Box>
              </Container>
            ))
          )}
        </SpaceBetween>
      </Modal>
    </Container>
  );
}

export default DocumentUpload;
